import Moment from 'moment';

export interface ClaimSummary {
  readonly id: string;
  readonly claimNumber: string;
  readonly description: string;
  readonly statusLabel: string;
  readonly filedOn: string;
  readonly lastUpdatedOn: string;
  readonly handler: string;
}

const OPEN = 'OPEN';
const FNOL = 'FNOL';

/** How an FNOL row reads in the policyholder's own claims list, before Claim Ops accepts it. */
export const REPORTED_LABEL = 'Reported, under review';

const text = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

/** Claim status arrives as 'OPEN' / 'CLOSED' / 'NOTICE ONLY'. */
const isOpen = (claim: any): boolean =>
  text(claim?.status).toUpperCase() === OPEN;

const timeOf = (value: unknown): number => {
  const moment = value ? Moment(value as Moment.MomentInput) : null;
  return moment?.isValid() ? moment.valueOf() : 0;
};

/** "Aug 21, 2026"; empty when there is no usable date to show. */
export function formatClaimDate(value: unknown): string {
  if (!value) {
    return '';
  }
  const moment = Moment(value as Moment.MomentInput);
  return moment.isValid() ? moment.format('MMM D, YYYY') : '';
}

/**
 * The adjuster is the claim's handler, and a handler is assigned as the claim
 * progresses -- so the presence of one is a real state worth surfacing, not a
 * label invented for the tile. Without a handler the claim's own status is
 * shown instead of implying an assignment that has not happened.
 */
export function getStatusLabel(claim: any): string {
  const handler = getHandlerName(claim);
  if (handler) {
    return 'Adjuster assigned';
  }
  const status = text(claim?.status);
  if (!status) {
    return '';
  }
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
}

export function getHandlerName(claim: any): string {
  return [text(claim?.handlerFirstName), text(claim?.handlerLastName)]
    .filter(Boolean)
    .join(' ');
}

/**
 * A single FNOL claim, described for the policyholder's own claims list: it is
 * a technical record Claim Ops has not accepted yet, so it reads with a fixed
 * label rather than the claim's own status. Returns null for anything else --
 * the dashboard's "latest open claim" tile has no use for it, since an FNOL
 * claim is not yet open in the business sense.
 */
export function describeReportedClaim(claim: unknown): ClaimSummary | null {
  const record = claim as any;
  if (text(record?.status).toUpperCase() !== FNOL) {
    return null;
  }

  const claimNumber = text(record?.claimNumber);
  const id = text(record?.id) || claimNumber;
  const description = text(record?.description) || text(record?.incidentType);
  const filedOn = formatClaimDate(record?.reportedDate ?? record?.created);
  const lastUpdatedOn = formatClaimDate(record?.modified);
  const handler = getHandlerName(record);

  return {
    id,
    claimNumber,
    description,
    statusLabel: REPORTED_LABEL,
    filedOn,
    lastUpdatedOn,
    handler,
  };
}

/**
 * The open claim the insured most recently saw movement on. Closed and
 * notice-only claims are excluded: the tile exists to surface the claim a
 * policyholder may still need to act on.
 */
export function selectLatestOpenClaim(claims: unknown): ClaimSummary | null {
  const list = Array.isArray(claims) ? claims : [];
  const open = list.filter(isOpen);

  if (!open.length) {
    return null;
  }

  const latest = open.reduce((newest: any, claim: any) =>
    timeOf(claim?.modified) > timeOf(newest?.modified) ? claim : newest
  );

  return {
    id: text(latest?.id) || text(latest?.claimNumber),
    claimNumber: text(latest?.claimNumber),
    // `description` is the insured-facing summary; incident type is the
    // fallback so the row is never left blank.
    description: text(latest?.description) || text(latest?.incidentType),
    statusLabel: getStatusLabel(latest),
    filedOn: formatClaimDate(latest?.reportedDate ?? latest?.created),
    lastUpdatedOn: formatClaimDate(latest?.modified),
    handler: getHandlerName(latest),
  };
}

/** Unwraps the claims endpoint's `{ data: hits, meta }` envelope. */
export const readClaimHits = (payload: any): unknown[] => {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (Array.isArray(payload?.data)) {
    return payload.data;
  }
  return [];
};
